//#region types
import { TimeReportingProjectMemberFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import { EditableTableFieldProps, FormFieldDefinitionType } from "models/propTypes/genericFormProps";
import { CurrencyEnums, FormEnums } from "models/enums";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModelTimeReporting as ApiModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import CategoryBillingDetailsField from "components/timeReportingProject/_partial/categoryBillingDetailsField";
import { Grid } from "@mui/material";
//#endregion
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useMemberRatesFields(
  props: TimeReportingProjectMemberFormViewProps
): FormFieldDefinitionType<TimeReportingProjectFormTypes.ProjectMember> {
  const { project } = props;

  const getBillingModels = () => {
    const billingModels = EnumHelper.getNumericItemValues(ApiModel.TimeReportingPersonProjectBillingModel).map(
      (item) =>
        ({
          value: LocalizationHelperInstance.getTranslatedEnumValue("TimeReportingProject.BillingModel", item),
          data: item,
          id: item,
        } as AutocompleteListItem)
    );

    return billingModels;
  };

  return {
    categoryRates: {
      label: "",
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) => !values.categories?.some((cm) => cm.selected),
      innerFields: {
        selected: {
          label: "",
          type: FormEnums.FormFieldType.CHECKBOX,
          classes: {
            input: props.isNew ?? true ? cssExports.disable : "",
            formControl: cssExports.select,
          },
          disabled: (values: TimeReportingProjectFormTypes.MemberRate) => props.isNew ?? true,
        },
        name: {
          label: "",
          type: FormEnums.FormFieldType.TEXT,
          readonly: true,
          classes: { formControl: cssExports.truncateName },
        },
        individualBillingModelName: {
          label: " ",
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          widthColumnCount: 5,
          extraProps: {
            items: getBillingModels(),
            referenceFieldName: "individualBillingModel",
            onItemSelect: props.onIndividualBillingModelChange,
          },
          disabled: (values: TimeReportingProjectFormTypes.CategoryRate) => !values.selected,
        },
        hourlyRate: {
          label: " ",
          type: FormEnums.FormFieldType.CURRENCY,
          rightAlign: true,
          extraProps: {
            currency: project?.currency ?? CurrencyEnums.CurrencyCode.SEK,
          },
          onChangeHandler: props.onHourlyRateChange,
          disabled: (values: TimeReportingProjectFormTypes.CategoryRate) => !values.selected,
        },
        hours: {
          label: " ",
          type: FormEnums.FormFieldType.NUMBER,
          rightAlign: true,
          extraProps: { suffix: "h", decimalPoints: 2, hideValueIfReadOnly: true },
          widthColumnCount: 2,
          readonly: (values: TimeReportingProjectFormTypes.CategoryRate) => {
            return values.individualBillingModel === ApiModel.TimeReportingPersonProjectBillingModel.Hourly;
          },
          onChangeHandler: props.onCategoryRateHoursChange,
          disabled: (values: TimeReportingProjectFormTypes.CategoryRate) => !values.selected,
        },
      },
      className: cssExports.categoryMemeberContainer,
      extraProps: {
        isCompactTable: true,
        selectionFieldName: "selected",
        hideActions: true,
        disableSelection: props.isNew ?? true,
        showColumnHeaders: true,
        headers: [
          {
            label: "",
            fieldName: "selected",
          },
          {
            fieldName: "name",
            label: LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.TimeReportingProject.ChangeAllMemberRates
            ),
            columnSpan: 4,
          },
        ],
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
    changesApplyFrom: {
      label: TranslationKeys.TimeReportingProject.ChangesApplyFromTheFirstInTheMonth,
      controlLabel: " ",
      type: FormEnums.FormFieldType.MONTH,
      extraProps: {
        hideClearable: true,
        popperPositionFixed: true,
      },
      onChangeHandler: props.onChangesApplyMonthChange,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        props.isNew || !values.categoryRates?.some((cr) => cr.selected),
    },
    futureCategoryRates: {
      label: TranslationKeys.TimeReportingProject.QueuedRateChanges,
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectMember) =>
        props.isNew || values.futureCategoryRates === undefined || values.futureCategoryRates.length === 0,
      innerFields: {
        name: {
          label: " ",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: <CategoryBillingDetailsField currency={props.project?.currency}></CategoryBillingDetailsField>,
          readonly: true,
        },
        validFromName: {
          label: "",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: (
            <Grid container justifyContent="flex-end">
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.ValidFrom)}
            </Grid>
          ),
          rightAlign: true,
          classes: { formControl: cssExports.validFromFieldContainer },
        },
        validFrom: {
          label: " ",
          type: FormEnums.FormFieldType.MONTH,
          readonly: true,
          extraProps: {
            popperPositionFixed: true,
            hideClearable: true,
          },
        },
      },
      extraProps: {
        isCompactTable: true,
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectMember>,
    },
  };
}

export default useMemberRatesFields;
